/**
 * Página /superadmin/saldo-apertura — carga inicial one-time.
 *
 * Siembra el "saldo de apertura del barrio": crea el cierre anual del
 * año anterior al go-live con su saldo bancario al 31-dic (según
 * extracto). Eso da la apertura del primer año real para el cuadre de
 * flujo de caja y mueve el año contable activo al año del go-live.
 *
 * Es de una sola vez. Del go-live en adelante, el cierre anual y su
 * saldo los opera el tesorero desde el portal (Banco).
 */
import { useState } from 'react';
import { ApiError } from '../../lib/api/client';
import {
  sembrarSaldoApertura,
  type SaldoAperturaResultado,
} from '../../lib/api/cargas';
import { formatCLP } from '../../lib/format';
import { Button } from '../../components/ui/Button';
import { PageHeader } from '../../components/layout/PageHeader';

const ANIO_MIN = 2000;
const ANIO_MAX = 2100;

export function SaldoAperturaPage() {
  const [anioStr, setAnioStr] = useState('');
  const [saldoStr, setSaldoStr] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resultado, setResultado] = useState<SaldoAperturaResultado | null>(
    null,
  );

  const anioNum = parseInt(anioStr, 10);
  const anioValido =
    /^\d{4}$/.test(anioStr.trim()) &&
    anioNum >= ANIO_MIN &&
    anioNum <= ANIO_MAX;

  const saldoNum = parseInt(saldoStr.replace(/\D/g, ''), 10);
  const saldoSigno = saldoStr.trim().startsWith('-') ? -1 : 1;
  const saldoValido = saldoStr.trim().length > 0 && !isNaN(saldoNum);
  const saldoFinal = saldoValido ? saldoNum * saldoSigno : null;

  const puedeEnviar = anioValido && saldoValido && !submitting;

  async function handleSembrar() {
    if (!puedeEnviar || saldoFinal === null) return;
    setSubmitting(true);
    setError(null);
    setResultado(null);
    try {
      const res = await sembrarSaldoApertura(anioNum, saldoFinal);
      setResultado(res);
    } catch (err) {
      if (err instanceof ApiError) {
        const detail =
          typeof err.body === 'object' &&
          err.body !== null &&
          'detail' in err.body
            ? String((err.body as { detail: unknown }).detail)
            : `Error ${err.status}`;
        setError(detail);
      } else {
        setError('Error de red');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="px-8 py-6 space-y-6">
      <PageHeader
        eyebrow="Carga inicial LIVE"
        title="Saldo de apertura"
        description="Siembra el saldo bancario de apertura del barrio creando el cierre del año anterior al go-live. Acción de una sola vez."
      />

      <div className="bg-bg-elevated border-l-4 border-admin-red rounded-sm p-4 max-w-3xl text-sm text-text-secondary space-y-2">
        <div className="font-medium text-text-primary">Qué hace:</div>
        <ul className="list-disc list-inside space-y-1 pl-2">
          <li>
            Crea el cierre anual del año que indiques con su saldo bancario
            (totales de arrastre en cero — no hay deuda previa al sistema).
          </li>
          <li>
            Ese saldo queda como <strong>apertura</strong> del primer año real
            en el cuadre de flujo de caja (Banco, portal del tesorero).
          </li>
          <li>
            Mueve el <strong>año contable activo</strong> al año siguiente
            (el del go-live).
          </li>
        </ul>
        <p className="text-xs text-text-muted pt-1">
          Es de una sola vez. Si ya existe un cierre para ese año, la operación
          se rechaza (no pisa un cierre real).
        </p>
      </div>

      <div className="space-y-4 max-w-md">
        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">
            Año a sembrar (el anterior al go-live, ej. 2025)
          </span>
          <input
            type="text"
            inputMode="numeric"
            value={anioStr}
            onChange={(e) => setAnioStr(e.target.value)}
            placeholder="Ej. 2025"
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
          />
        </label>

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">
            Saldo del banco al 31-dic de ese año (CLP)
          </span>
          <input
            type="text"
            inputMode="numeric"
            value={saldoStr}
            onChange={(e) => setSaldoStr(e.target.value)}
            placeholder="Ej. 2485000 (o -50000 si hubo sobregiro)"
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
          />
          {saldoFinal !== null && (
            <span className="text-xs text-text-muted block mt-1">
              Se registrará: {formatCLP(saldoFinal)}
            </span>
          )}
        </label>

        <Button
          variant="primary"
          onClick={handleSembrar}
          disabled={!puedeEnviar}
        >
          {submitting ? 'Sembrando…' : 'Sembrar saldo de apertura'}
        </Button>
      </div>

      {error && (
        <div
          className="bg-admin-red/15 border-l-4 border-admin-red rounded-sm p-3 max-w-3xl text-sm text-text-primary"
          role="alert"
        >
          {error}
        </div>
      )}

      {resultado && (
        <div
          className="bg-bg-elevated border-l-4 border-emerald-500 rounded-sm p-4 max-w-3xl text-sm text-text-secondary space-y-1"
          role="status"
        >
          <div className="font-medium text-text-primary">
            Saldo de apertura sembrado.
          </div>
          <p>
            Cierre {resultado.anio} creado con saldo{' '}
            <span className="text-text-primary">
              {formatCLP(resultado.saldo_final_banco ?? 0)}
            </span>
            . El año contable activo ahora es{' '}
            <span className="text-text-primary">{resultado.anio + 1}</span>.
          </p>
        </div>
      )}
    </div>
  );
}
